import { useMemo } from 'react';
import { useConnection, useReadContract } from 'wagmi';

import { TRANCHE_SHARE_DECIMALS } from '@/app/shared/constants/common';
import { VaultTrancheId } from '@/app/shared/constants/vaults';
import { getTrancheAddressById } from '@/app/shared/constants/web3/chainContracts';
import { ABI } from '@/app/shared/constants/web3/constants';
import { useChainContracts } from '@/app/shared/hooks/blockchain/useChainContracts';
import {
  SequentialStepPlan,
  useSendSequentialSteps,
} from '@/app/shared/hooks/blockchain/useSendSequentialSteps/useSendSequentialSteps';
import { useDebounce } from '@/app/shared/hooks/useDebounce';
import { safeParseUnits } from '@/app/shared/lib/safeParseUnits';

interface UseVaultWithdrawSequentialArgs {
  amount: string;
  trancheId: VaultTrancheId;
  vaultId?: string;
  trancheSymbol?: string;
  enabled?: boolean;
}

/**
 * Non-batch withdraw stepper.
 * Instant: redeem shares → MZUSD, then redeem MZUSD → USDC.
 * Delayed: requestWithdrawal shares only (finish claim later in Portfolio).
 * If user stops after step 1 of instant, finish via Portfolio Redeem MZUSD.
 */
export const useVaultWithdrawSequential = ({
  amount,
  trancheId,
  vaultId,
  trancheSymbol = 'tranche',
  enabled = true,
}: UseVaultWithdrawSequentialArgs) => {
  const { address, isConnected } = useConnection();
  const contracts = useChainContracts(vaultId);
  const debouncedAmount = useDebounce(amount, 500);

  const sharesAmount = safeParseUnits(debouncedAmount, TRANCHE_SHARE_DECIMALS);
  const trancheAddress = contracts ? getTrancheAddressById(contracts, trancheId) : undefined;

  const { data: directWithdrawEnabled } = useReadContract({
    address: trancheAddress,
    abi: ABI.TRANCHE,
    functionName: 'directWithdrawEnabled',
    query: {
      enabled: enabled && !!trancheAddress,
    },
  });

  const isInstantWithdraw = directWithdrawEnabled === true;

  const { data: previewAssets } = useReadContract({
    address: trancheAddress,
    abi: ABI.TRANCHE,
    functionName: 'previewRedeem',
    args: sharesAmount && sharesAmount > 0n ? [sharesAmount] : [0n],
    query: {
      enabled: enabled && !!trancheAddress && !!sharesAmount && sharesAmount > 0n && isInstantWithdraw,
    },
  });

  const plannedSteps = useMemo((): SequentialStepPlan[] => {
    if (!enabled || !contracts || !sharesAmount || !trancheAddress || directWithdrawEnabled === undefined) {
      return [];
    }

    const redeemShares: SequentialStepPlan = {
      id: 'redeem-shares',
      title: `Redeem ${trancheSymbol}`,
      description: `Redeem ${trancheSymbol} shares to receive MZUSD`,
      isComplete: false,
      tx: {
        address: trancheAddress,
        abi: ABI.TRANCHE,
        functionName: 'requestWithdrawal',
        args: [sharesAmount],
      },
    };

    if (!isInstantWithdraw) {
      return [redeemShares];
    }

    if (!previewAssets || previewAssets === 0n) return [];

    return [
      redeemShares,
      {
        id: 'redeem-mzusd',
        title: 'Redeem MZUSD',
        description: 'Redeem MZUSD to receive USDC',
        isComplete: false,
        tx: {
          address: contracts.MZUSD,
          abi: ABI.MZUSD,
          functionName: 'requestWithdrawal',
          args: [previewAssets],
        },
      },
    ];
  }, [
    contracts,
    directWithdrawEnabled,
    enabled,
    isInstantWithdraw,
    previewAssets,
    sharesAmount,
    trancheAddress,
    trancheSymbol,
  ]);

  const expectedTxCount = isInstantWithdraw ? 2 : 1;
  const planKey = `${debouncedAmount}:${trancheId}:${trancheAddress ?? ''}:${address ?? ''}:${isInstantWithdraw ? 'i' : 'd'}`;

  const sequential = useSendSequentialSteps({
    steps: plannedSteps,
    planKey,
    enabled: enabled && isConnected && plannedSteps.length === expectedTxCount,
  });

  const isReady =
    enabled &&
    isConnected &&
    !!address &&
    directWithdrawEnabled !== undefined &&
    plannedSteps.length === expectedTxCount &&
    !!sharesAmount;

  return {
    ...sequential,
    sharesAmount,
    previewAssets,
    trancheAddress,
    contracts,
    debouncedAmount,
    isReady,
    isInstantWithdraw,
    directWithdrawEnabled,
    isBatchingTransaction: false as const,
  };
};
